// src/components/StrategyBuilder/StepStrategyInfo.jsx
import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Label as LabelIcon,
  Description as DescIcon,
} from '@mui/icons-material';

const TIMEFRAMES = [
  { value: '1m', label: '1 Minute' },
  { value: '3m', label: '3 Minutes' },
  { value: '5m', label: '5 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '2h', label: '2 Hours' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
];

const StepStrategyInfo = ({ data, onChange }) => {
  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LabelIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800, 
              color: '#1e293b',
              mb: 0.5,
            }}
          >
            Strategy Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure basic strategy settings
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 4 }} />

      {/* Basic Information Card */}
      <Card sx={{ borderRadius: 3, border: '2px solid #e2e8f0', mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#1e293b',
            }}
          >
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            {/* Strategy ID */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Strategy ID *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., rth_zero_1"
                value={data.strategy_id || ''}
                onChange={(e) => onChange({ ...data, strategy_id: e.target.value })}
                required
                helperText="Unique identifier for your strategy (use lowercase and underscores)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontWeight: 600,
                    fontSize: '1rem',
                    fontFamily: 'monospace',
                  },
                }}
              />
            </Grid>

            {/* Strategy Name */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Display Name
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., RTH Zero 1 Strategy"
                value={data.strategy_name || ''}
                onChange={(e) => onChange({ ...data, strategy_name: e.target.value })}
                helperText="Friendly name for display purposes"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontWeight: 600,
                    fontSize: '1rem',
                  },
                }}
              />
            </Grid>

            {/* Symbol */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Symbol *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., QQQ, BTCUSD, ES"
                value={data.symbol || ''}
                onChange={(e) => onChange({ ...data, symbol: e.target.value.toUpperCase() })}
                required
                helperText="Trading symbol/ticker"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                  },
                }}
              />
            </Grid>

            {/* Timeframe */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Timeframe *
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={data.timeframe || '1m'}
                  onChange={(e) => onChange({ ...data, timeframe: e.target.value })}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                      borderWidth: 2,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                    '& .MuiSelect-select': {
                      fontWeight: 600,
                    },
                  }}
                >
                  {TIMEFRAMES.map((tf) => (
                    <MenuItem key={tf.value} value={tf.value}>
                      {tf.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Description (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Describe your trading strategy, entry/exit conditions, risk management rules, etc..."
                value={data.description || ''}
                onChange={(e) => onChange({ ...data, description: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontFamily: 'inherit',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                  mt: 1,
                  display: 'block',
                  fontStyle: 'italic',
                }}
              >
                Include notes about your strategy logic, timing windows, or any other relevant details
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: '#eff6ff',
          border: '2px solid #bfdbfe',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <DescIcon sx={{ color: '#3b82f6', mt: 0.3 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e3a8a', mb: 0.5 }}>
              About Strategies
            </Typography>
            <Typography variant="body2" sx={{ color: '#1e40af' }}>
              A strategy tracks multiple signals and conditions to trigger alerts based on your trading logic.
              You'll define expected signals, configure trigger conditions, and set timing parameters in the next steps.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default StepStrategyInfo;
